import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { listOrgMemberDirectory, type OrgMemberDirectoryEntry } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { addWorkspaceMember } from "../api/rag-api.js";
import type { ApiWorkspaceUser } from "../api/types.js";
import { formatOrgRoleLabel, mapOrgRoleToWorkspaceRole } from "../lib/org-role-map.js";

export type AddedWorkspaceMember = ApiWorkspaceUser;

interface AddWorkspaceMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  ownerId: string;
  existingMemberIds: string[];
  onAdded: (added: AddedWorkspaceMember[]) => Promise<void>;
}

function matchesMemberSearch(entry: OrgMemberDirectoryEntry, query: string) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  return (
    (entry.name ?? "").toLowerCase().includes(normalized) ||
    entry.email.toLowerCase().includes(normalized)
  );
}

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatAddMemberError(message: string): string {
  const normalized = message.toLowerCase();

  if (normalized.includes("user not found") || normalized.includes("not_found")) {
    return "Could not provision user — contact support";
  }

  if (
    normalized.includes("member management access required") ||
    normalized.includes("forbidden") ||
    normalized.includes("access denied")
  ) {
    return "You don't have permission to add members";
  }

  if (
    normalized.includes("already exists under a different account") ||
    normalized.includes("unique constraint") ||
    normalized.includes("internal server error")
  ) {
    return "User already exists under a different account";
  }

  return message;
}

export function AddWorkspaceMembersDialog({
  open,
  onOpenChange,
  workspaceId,
  ownerId,
  existingMemberIds,
  onAdded,
}: AddWorkspaceMembersDialogProps) {
  const { syncSession } = useAuth();
  const { showToast } = useToast();
  const [directory, setDirectory] = useState<OrgMemberDirectoryEntry[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const excludedIds = useMemo(
    () => new Set([ownerId, ...existingMemberIds]),
    [ownerId, existingMemberIds]
  );

  const availableMembers = useMemo(
    () => directory.filter((entry) => !excludedIds.has(entry.userId)),
    [directory, excludedIds]
  );

  const filteredMembers = useMemo(
    () => availableMembers.filter((entry) => matchesMemberSearch(entry, searchQuery)),
    [availableMembers, searchQuery]
  );

  useEffect(() => {
    if (!open) return;

    setSelectedUserIds([]);
    setSearchQuery("");
    setError(null);

    async function loadDirectory() {
      setLoadingDirectory(true);
      try {
        const { members } = await listOrgMemberDirectory();
        setDirectory(members);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load organization members");
        setDirectory([]);
      } finally {
        setLoadingDirectory(false);
      }
    }

    void loadDirectory();
  }, [open]);

  function toggleUser(userId: string) {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }

  const canAdd = selectedUserIds.length > 0 && !saving && !loadingDirectory;

  async function handleAddSelected() {
    if (!canAdd) return;

    setSaving(true);
    setError(null);

    await syncSession();

    const failures: string[] = [];
    const addedMembers: AddedWorkspaceMember[] = [];
    let successCount = 0;

    try {
      for (const userId of selectedUserIds) {
        const entry = directory.find((member) => member.userId === userId);
        if (!entry?.email) {
          failures.push(`${userId}: missing member email`);
          continue;
        }
        try {
          const { workspaceUser } = await addWorkspaceMember(workspaceId, {
            userId: entry.userId,
            email: entry.email,
            role: mapOrgRoleToWorkspaceRole(entry.orgRole),
          });
          addedMembers.push(workspaceUser);
          successCount += 1;
        } catch (err) {
          const label = entry?.name || entry?.email || userId;
          const rawMessage = err instanceof Error ? err.message : "Failed to add member";
          failures.push(`${label}: ${formatAddMemberError(rawMessage)}`);
        }
      }

      if (successCount === 0) {
        const errorMessage = failures.join("\n");
        setError(errorMessage);
        showToast("Could not add members", "error");
        return;
      }

      await onAdded(addedMembers);

      if (failures.length > 0) {
        setError(`Some members could not be added:\n${failures.join("\n")}`);
        setSelectedUserIds([]);
        return;
      }

      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex h-full flex-col p-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Add members to workspace</SheetTitle>
          <SheetDescription className={pageDescriptionMobileHiddenClassName}>
            Select organization members to add to this workspace.
          </SheetDescription>
        </SheetHeader>

        <div className="border-b border-white/10 px-6 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search organization members…"
              className="pl-9"
              aria-label="Search organization members"
              disabled={loadingDirectory}
            />
          </div>
        </div>

        <SheetBody className="min-h-0 flex-1 px-6 py-4">
          {loadingDirectory ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
            </div>
          ) : availableMembers.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              All organization members are already on this workspace.
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              No members match &ldquo;{searchQuery.trim()}&rdquo;
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[var(--lm-border-subtle)] bg-white shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-slate-950/40 dark:shadow-none">
              <ul className="divide-y divide-[var(--lm-border-subtle)] dark:divide-white/5">
                {filteredMembers.map((entry) => {
                  const selected = selectedUserIds.includes(entry.userId);
                  const displayName = entry.name || entry.email;

                  return (
                    <li key={entry.userId}>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => toggleUser(entry.userId)}
                        className={cn(
                          "h-auto w-full justify-start gap-3 rounded-none px-4 py-3 text-left text-sm font-normal text-[var(--lm-foreground-strong)] dark:text-white",
                          selected ? "bg-violet-500/10 hover:bg-violet-500/15" : "hover:bg-[var(--lm-hover)] dark:hover:bg-white/5"
                        )}
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--avatar-user-bg)] text-[11px] font-semibold text-[var(--avatar-user-fg)] dark:bg-violet-500/30 dark:text-white">
                          {initials(displayName)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">{displayName}</div>
                          <div className="truncate text-xs text-[var(--lm-muted)] dark:text-slate-500">{entry.email}</div>
                          <div className="truncate text-xs text-[var(--lm-muted)] dark:text-slate-600">
                            {formatOrgRoleLabel(entry.orgRole)}
                          </div>
                        </div>
                        <div
                          className={cn(
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors",
                            selected
                              ? "border-violet-400 bg-violet-500 text-white"
                              : "border-[var(--lm-border-subtle)] bg-transparent dark:border-white/20"
                          )}
                        >
                          {selected ? <Check className="h-3 w-3" /> : null}
                        </div>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </SheetBody>

        {error ? (
          <div className="border-t border-white/10 px-6 py-3">
            <p className="whitespace-pre-line text-sm text-red-400">{error}</p>
          </div>
        ) : null}

        <SheetFooter>
          <Button type="button" variant="surface" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={() => void handleAddSelected()} disabled={!canAdd} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Add selected
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
